import React from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router";
import { Link, useHistory, NavLink } from "react-router-dom";
import { createPortal } from "react-dom";
import {
	FiHome,
	FiTrello,
	FiClock,
	FiUser,
	FiExternalLink,
	FiFolder,
	FiCalendar,
	FiX,
	FiMenu,
	FiHelpCircle,
	FiChevronRight,
	FiPlus,
	FiChevronDown,
	FiChevronUp,
	FiSettings,
	FiCreditCard,
	FiLock,
	FiRepeat,
} from "react-icons/fi";
import { PiTimer } from "react-icons/pi";
import { RxBell } from "react-icons/rx";
import { Popover, OverlayTrigger, Tooltip } from "react-bootstrap";
import { isAdmin, isSupervisor, hasSubscriptionFeature } from "utils/utils";
import {
	authService,
	alertNotificationsService as notificationsService,
	notificationsSocketService,
} from "services";
import moment from "moment";
import Avatar from "components/Avatar";
import Logo from "components/Logo";
import { getHelpUrl } from "utils/utils";
import { isHaddockEnvironment } from "utils/documentUtils";
import { useTranslation } from "react-i18next";
import { CreateCompanyModal } from "components/Companies";
import UpgradeModal from "components/Modal/UpgradeModal";
import { MdOutlineWorkspacePremium } from "react-icons/md";
import { OnboardingSidebarCard } from "components/Onboarding";

const getMobileWeekPlanPath = (user) => {
	const groupId =
		user?.employee_with_group?.group?.id ||
		(user?.groups_supervised?.length > 0 ? user.groups_supervised[0].id : null);
	if (!groupId) {
		return "/plans/";
	}
	const start = moment().startOf("isoWeek").format("YYYY-MM-DD");
	const end = moment().endOf("isoWeek").format("YYYY-MM-DD");
	return `/plans/${groupId}/${start}@${end}`;
};

const isPlanWeekRoute = (pathname) =>
	/^\/plans\/\d+\/\d{4}-\d{2}-\d{2}@\d{4}-\d{2}-\d{2}/.test(pathname || "");

/** Mismo criterio que `Layout`: ancho < 988px → menú hamburguesa y sidebar estrecho. */
const LAYOUT_COMPACT_NAV_MAX_WIDTH_PX = 987;

const useLayoutCompactNav = () => {
	const [compact, setCompact] = React.useState(
		() =>
			typeof window !== "undefined" &&
			window.matchMedia(`(max-width: ${LAYOUT_COMPACT_NAV_MAX_WIDTH_PX}px)`)
				.matches
	);
	React.useEffect(() => {
		const mq = window.matchMedia(
			`(max-width: ${LAYOUT_COMPACT_NAV_MAX_WIDTH_PX}px)`
		);
		const onChange = () => setCompact(mq.matches);
		onChange();
		mq.addEventListener("change", onChange);
		return () => mq.removeEventListener("change", onChange);
	}, []);
	return compact;
};

const Sidebar = (props) => {
	// console.log(props);
	const { t } = useTranslation();
	const { user, isTrialEnded } = props;
	// console.log(user);

	// Estado para el modal de upgrade
	const [showUpgradeModal, setShowUpgradeModal] = React.useState(false);
	const [upgradeFeature, setUpgradeFeature] = React.useState("");

	const helpUrl = getHelpUrl();
	const history = useHistory();
	const domain = window.location.hostname;
	const layoutCompactNav = useLayoutCompactNav();

	// Estado para el selector de empresa
	const [isCompanySelectorOpen, setIsCompanySelectorOpen] =
		React.useState(false);
	const [dropdownPosition, setDropdownPosition] = React.useState({
		top: 0,
		left: 0,
	});

	// Estado para el modal de crear empresa
	const [isCreateCompanyModalOpen, setIsCreateCompanyModalOpen] =
		React.useState(false);

	// Estado reactivo para las empresas
	const [companies, setCompanies] = React.useState(
		authService.getAllCompanyTokens() || []
	);

	// Estado para el contador de notificaciones no leídas
	const [unreadNotificationsCount, setUnreadNotificationsCount] =
		React.useState(0);

	// Efecto para prevenir scroll del body cuando el modal está abierto
	React.useEffect(() => {
		if (isCreateCompanyModalOpen) {
			document.body.style.overflow = "hidden";
		} else {
			document.body.style.overflow = "unset";
		}

		// Cleanup al desmontar el componente
		return () => {
			document.body.style.overflow = "unset";
		};
	}, [isCreateCompanyModalOpen]);

	// Cargar contador de notificaciones no leídas
	React.useEffect(() => {
		const loadUnreadNotificationsCount = async () => {
			try {
				const thirtyDaysAgo = moment()
					.subtract(30, "days")
					.format("YYYY-MM-DD");
				const response = await notificationsService.getAll(thirtyDaysAgo);

				if (response.ok && response.data) {
					const unreadCount = response.data.filter((n) => !n.read).length;
					setUnreadNotificationsCount(unreadCount);
				} else {
					setUnreadNotificationsCount(0);
				}
			} catch (error) {
				console.error("Error al cargar contador de notificaciones:", error);
				setUnreadNotificationsCount(0);
			}
		};

		if (user && user.id) {
			loadUnreadNotificationsCount();

			// El WebSocket ya está conectado y suscrito al topic desde App.js
			// Registramos un callback para actualizar el contador cuando llegue una notificación
			const notificationCallback = (notification) => {
				setUnreadNotificationsCount((prevCount) => {
					// Si la notificación no está leída, incrementar el contador
					if (!notification.read) {
						return prevCount + 1;
					}
					return prevCount;
				});
			};

			// Callback para cuando se marca una notificación como leída/no leída
			const readChangedCallback = (notificationId, isRead) => {
				setUnreadNotificationsCount((prevCount) => {
					// Si se marca como leída, decrementar el contador
					// Si se marca como no leída, incrementar el contador
					return isRead ? Math.max(0, prevCount - 1) : prevCount + 1;
				});
			};

			notificationsSocketService.registerCallback(notificationCallback);
			notificationsSocketService.registerReadChangedCallback(
				readChangedCallback
			);

			// Cleanup: desregistrar los callbacks cuando el componente se desmonte
			return () => {
				notificationsSocketService.unregisterCallback(notificationCallback);
				notificationsSocketService.unregisterReadChangedCallback(
					readChangedCallback
				);
			};
		}
	}, [user]);

	// Detectar si es usuario de Haddock (debe ir antes de usarse)
	const isHaddockUser = isHaddockEnvironment();

	// Función para generar colores consistentes para empresas (debe ir antes de usarse)
	const getCompanyColor = (companyName) => {
		const colors = [
			"#8B5CF6", // Púrpura
			"#3B82F6", // Azul
			"#10B981", // Verde
			"#F59E0B", // Amarillo
			"#EF4444", // Rojo
			"#EC4899", // Rosa
			"#06B6D4", // Cian
			"#84CC16", // Lima
			"#F97316", // Naranja
			"#6366F1", // Índigo
		];

		// Generar un índice basado en el nombre de la empresa
		let hash = 0;
		for (let i = 0; i < companyName.length; i++) {
			hash = companyName.charCodeAt(i) + ((hash << 5) - hash);
		}
		return colors[Math.abs(hash) % colors.length];
	};

	const currentCompany =
		companies.find(
			(company) => company.company_name === authService.currentCompanyId
		) || companies[0];

	// Efecto para actualizar empresas cuando cambien en authService o localStorage
	React.useEffect(() => {
		// Función para actualizar las empresas
		const updateCompanies = () => {
			const newCompanies = authService.getAllCompanyTokens() || [];
			setCompanies(newCompanies);
		};

		// Si no hay empresas pero hay token con company_name, procesarlo
		if (
			companies.length === 0 &&
			authService.token &&
			authService.token.company_name
		) {
			authService.setCompanyTokens(authService.token);
			updateCompanies();
		}

		// Escuchar cambios en localStorage (cuando otro tab actualiza)
		const handleStorageChange = (e) => {
			if (e.key === "companyTokens") {
				updateCompanies();
			}
		};

		window.addEventListener("storage", handleStorageChange);

		// Polling solo para usuarios de Haddock (para detectar cuando el SSO termine de cargar tokens)
		let interval = null;
		if (isHaddockUser) {
			interval = setInterval(() => {
				const currentTokens = authService.getAllCompanyTokens() || [];
				if (currentTokens.length !== companies.length) {
					updateCompanies();
				}
			}, 500);
		}

		return () => {
			window.removeEventListener("storage", handleStorageChange);
			if (interval) {
				clearInterval(interval);
			}
		};
	}, [companies.length, isHaddockUser]);

	// Generar datos para el selector
	const selectedCompany = currentCompany
		? {
				name: currentCompany.company_name,
				initial: currentCompany.company_name.charAt(0).toUpperCase(),
				color: getCompanyColor(currentCompany.company_name),
			}
		: null;

	// Lógica para mostrar el selector
	const isUserAdmin = isAdmin(props.user);
	const shouldShowSelector = () => {
		if (isHaddockUser) {
			// Haddock: solo si tiene más de una company
			return companies.length > 1;
		}
		if (!isUserAdmin) {
			// No admin: solo si tiene más de una company
			return companies.length > 1;
		}
		// Admin: mostrar siempre (permite crear nueva empresa)
		return companies.length > 0;
	};

	const groups_supervised = user.groups_supervised;
	const isSupervisorWithEditEmployees =
		isSupervisor() &&
		groups_supervised.length > 0 &&
		groups_supervised.some((gs) => gs.edit_employees);
	const isSupervisorWithEditStaffing =
		isSupervisor() &&
		groups_supervised.length > 0 &&
		groups_supervised.some((gs) => gs.edit_staffing_levels);
	const isSupervisorWithEditGroups =
		isSupervisor() &&
		groups_supervised.length > 0 &&
		groups_supervised.some((gs) => gs.edit_groups);

	const isSupervisorWithEditDocuments =
		isSupervisor() &&
		groups_supervised.length > 0 &&
		groups_supervised.some((gs) => gs.edit_documents);

	const isSupervisorWithViewTimeRegistrations =
		isSupervisor() &&
		groups_supervised.length > 0 &&
		groups_supervised.some((gs) => gs.view_time_registrations ?? true);

	const allowTerminalFichajes =
		user?.employee_with_contract?.contract
			?.allow_time_registration_token_authentication;

	// Verificar si tiene acceso según suscripción
	const hasClockingTerminalSubscription =
		hasSubscriptionFeature("CLOCKING_TERMINAL");
	const hasPlanningSubscription = hasSubscriptionFeature("PLANNING");
	const hasMyCalendarSubscription = hasSubscriptionFeature("MY_CALENDAR");
	const hasExpensesSubscription = hasSubscriptionFeature("EXPENSES");

	// Mostrar el botón siempre, pero habilitar solo si tiene permisos
	const canAccessTerminalFichajes =
		(isAdmin() || allowTerminalFichajes) &&
		// hasClockingTerminalSubscription &&
		!isTrialEnded;

	const showEmployees = isAdmin() || isSupervisorWithEditEmployees;
	const showAdminDocuments = isAdmin() || isSupervisorWithEditDocuments;
	const showConfiguracion =
		isAdmin() ||
		isSupervisorWithEditEmployees ||
		isSupervisorWithEditStaffing ||
		isSupervisorWithEditGroups;

	const handleLogout = () => {
		authService.logout();
	};

	// Funciones para el selector de empresa
	const toggleCompanySelector = () => {
		if (!isCompanySelectorOpen) {
			// Calcular posición del dropdown cuando se abre
			const selectorElement = document.querySelector(".company-selector");
			const sidebarElement = document.querySelector(".sidebar");
			if (selectorElement && sidebarElement) {
				const rect = selectorElement.getBoundingClientRect();
				const isMini = sidebarElement.classList.contains("mini");

				if (isMini) {
					// En modo mini, centrar el dropdown respecto al icono
					setDropdownPosition({
						top: rect.bottom + 4,
						left: rect.left + rect.width / 2,
					});
				} else {
					// En modo normal, alinear a la izquierda
					setDropdownPosition({
						top: rect.bottom + 4,
						left: rect.left,
					});
				}
			}
		}
		setIsCompanySelectorOpen(!isCompanySelectorOpen);
	};

	const selectCompany = (company) => {
		console.log("🔄 Seleccionando empresa:", company.company_name);
		console.log("- Token antes del cambio:", authService.token);

		// Cambiar empresa usando authService
		const success = authService.switchCompany(company.company_name);
		console.log("- Switch exitoso:", success);

		if (success) {
			setIsCompanySelectorOpen(false);
		}
	};

	const handleCreateCompany = () => {
		setIsCompanySelectorOpen(false);
		setIsCreateCompanyModalOpen(true);
	};

	const handleCloseCreateCompanyModal = () => {
		setIsCreateCompanyModalOpen(false);
	};

	// Cerrar dropdown al hacer clic fuera
	React.useEffect(() => {
		const handleClickOutside = (event) => {
			if (
				isCompanySelectorOpen &&
				!event.target.closest(".company-selector-container")
			) {
				setIsCompanySelectorOpen(false);
			}
		};

		document.addEventListener("mousedown", handleClickOutside);
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, [isCompanySelectorOpen]);

	const handleTerminalAccess = async () => {
		const token = await authService.getTimeRegistrationToken();
		const maxAge = 60 * 60 * 24 * 30; // 30 días
		// console.log(token);
		if (token) {
			// Guarda la empresa actual
			if (currentCompany?.company_name) {
				const companyName = encodeURIComponent(currentCompany.company_name);
				document.cookie = `timeRegistrationCompany=${companyName}; path=/; max-age=${maxAge}; SameSite=Lax`;
			}

			//localStorage.setItem("timeRegistrationToken", token.access_token);
			document.cookie = `timeRegistrationToken=${token.access_token}; path=/; max-age=${maxAge}; SameSite=Lax`;
			authService.logout(null);

			history.push("/clockin");
		} else {
			console.log("Token no encontrado. Por favor, vuelva a iniciar sesión.");
		}
	};

	const popover = (
		<Popover id="popoverSidebar" className="popover-sidebar">
			<ul className="sidebar-bottom-menu">
				{!isTrialEnded && (
					<li className="option-profile">
						<Link className="sidebar-menu-item" to="/profile">
							{t("profile.profile")}
						</Link>
					</li>
				)}
				{!isTrialEnded && isAdmin() && (
					<>
						<li className="option-company">
							<Link className="sidebar-menu-item" to="/my-company">
								{t("profile.myCompany")}
							</Link>
						</li>
						{(domain.includes("plain") || domain.includes("localhost")) && (
							<li className="option-subscription">
								<Link className="sidebar-menu-item" to="/my-subscription">
									{t("profile.mySubscription")}
								</Link>
							</li>
						)}
					</>
				)}
				<li className="option-logout">
					<button
						className="sidebar-menu-item button-link"
						onClick={handleLogout}
					>
						{t("profile.logout")}
					</button>
				</li>
			</ul>
		</Popover>
	);

	const TipIcon = ({ id, tip, placement = "right", children }) => (
		<OverlayTrigger
			placement={placement}
			overlay={<Tooltip id={id}>{tip}</Tooltip>}
			trigger={["hover", "focus"]}
		>
			<span className="flex-center" tabIndex={0}>
				{children}
			</span>
		</OverlayTrigger>
	);

	return (
		<section className={`sidebar ${props.className || ""}`}>
			<div className="sidebar-main">
				<button
					className="btn-close-sidebar btn-transparent"
					onClick={props.handleClose}
					type="button"
				>
					<FiX />
				</button>

				<div className="sidebar-header">
					<OverlayTrigger
						placement="right"
						overlay={<Tooltip>{t("sidebar.goHome")}</Tooltip>}
					>
						<Link className="sidebar-logo-link" to="/">
							<Logo className="sidebar-logo" type="logo" />
							<Logo className="sidebar-iso" type="iso" />
						</Link>
					</OverlayTrigger>

					<button
						type="button"
						title="Menu"
						onClick={props.toggleNav}
						className="btn btn-transparent btn-menu"
					>
						<FiMenu />
					</button>
				</div>

				{/* Selector de empresa */}
				{shouldShowSelector() && (
					<div className="company-selector-container">
						<div className="company-selector" onClick={toggleCompanySelector}>
							{selectedCompany ? (
								<>
									<div
										className="company-icon"
										style={{ backgroundColor: selectedCompany.color }}
									>
										{selectedCompany.initial}
									</div>
									<span
										className="company-name substring"
										title={selectedCompany.name}
									>
										{selectedCompany.name}
									</span>
								</>
							) : (
								<>
									<div
										className="company-icon"
										style={{ backgroundColor: "#8B5CF6" }}
									>
										?
									</div>
									<span className="company-name">{t("company.noCompany")}</span>
								</>
							)}
							<div className="company-arrow">
								{isCompanySelectorOpen ? <FiChevronUp /> : <FiChevronDown />}
							</div>
						</div>

						{isCompanySelectorOpen && (
							<div
								className="company-dropdown"
								style={{
									top: `${dropdownPosition.top}px`,
									left: `${dropdownPosition.left}px`,
								}}
							>
								{/* Solo mostrar lista de empresas si hay más de una o si es usuario de Haddock */}
								{(companies.length > 1 || isHaddockUser) &&
									companies.map((company) => {
										const companyData = {
											name: company.company_name,
											initial: company.company_name.charAt(0).toUpperCase(),
											color: getCompanyColor(company.company_name),
										};

										const isSelected =
											currentCompany.company_name === company.company_name;

										return (
											<div
												key={company.company_name}
												className={`company-option ${
													isSelected ? "selected" : ""
												}`}
												onClick={
													isSelected ? undefined : () => selectCompany(company)
												}
												aria-disabled={isSelected}
												role="button"
												tabIndex={isSelected ? -1 : 0}
											>
												<div
													className="company-icon"
													style={{ backgroundColor: companyData.color }}
												>
													{companyData.initial}
												</div>
												<span className="company-name">{companyData.name}</span>
											</div>
										);
									})}

								{!isHaddockUser && isAdmin(props.user) && (
									<>
										{/* Solo mostrar separador si hay empresas en la lista */}
										{(companies.length > 1 || isHaddockUser) && (
											<div className="company-separator"></div>
										)}

										<div
											className="company-create"
											onClick={handleCreateCompany}
										>
											<FiPlus className="create-icon" />
											<span>{t("company.create")}</span>
										</div>
									</>
								)}
							</div>
						)}
					</div>
				)}

				<ul className="menu-core">
					{/* <li>
						<NavLink to="/" exact activeClassName="active">
							<TipIcon id="tt-home" tip="Inicio">
								<FiHome />
							</TipIcon>
							<span className="option-text">Inicio</span>
						</NavLink>
					</li> */}

					<li className="option-plan">
						{hasPlanningSubscription ? (
							<NavLink
								to={
									props.className === "mobile-sidebar" || layoutCompactNav
										? getMobileWeekPlanPath(user)
										: "/plans"
								}
								activeClassName="active"
								isActive={(match, location) => {
									if (
										props.className === "mobile-sidebar" ||
										layoutCompactNav
									) {
										return (
											isPlanWeekRoute(location.pathname) ||
											location.pathname === "/plans" ||
											location.pathname === "/plans/"
										);
									}
									return !!match;
								}}
							>
								<TipIcon id="tt-plans" tip={t("sidebar.planning")}>
									<FiTrello />
								</TipIcon>
								<span className="option-text">{t("sidebar.planning")}</span>
							</NavLink>
						) : (
							<a
								href="#"
								onClick={(e) => {
									e.preventDefault();
									setUpgradeFeature(t("sidebar.planning"));
									setShowUpgradeModal(true);
								}}
								className="disabled"
								style={{
									opacity: 0.5,
									cursor: "not-allowed",
								}}
							>
								<FiTrello />
								<span className="option-text">{t("sidebar.planning")}</span>
							</a>
						)}
					</li>

					<li>
						<NavLink
							to={
								isAdmin() || isSupervisorWithViewTimeRegistrations
									? "/attendance-supervisor"
									: "/my-times"
							}
							activeClassName="active"
						>
							<TipIcon id="tt-times" tip={t("sidebar.times")}>
								<FiClock />
							</TipIcon>
							<span className="option-text">{t("sidebar.times")}</span>
						</NavLink>
					</li>

					<li>
						<NavLink to="/absences" exact activeClassName="active">
							<TipIcon id="tt-absences" tip={t("sidebar.absences")}>
								<FiExternalLink />
							</TipIcon>
							<span className="option-text">
								{isAdmin() || isSupervisor()
									? t("sidebar.absences")
									: t("sidebar.myAbsences")}
							</span>
						</NavLink>
						{(isAdmin() || isSupervisor()) &&
							(props.context === "absences" ||
								props.className === "mobile-sidebar") && (
								<ul className="menu-sub">
									<li>
										<NavLink
											to="/pending-absences"
											exact
											activeClassName="active"
										>
											<span className="option-text">
												{t("sidebar.approveAbsences")}
											</span>
										</NavLink>
									</li>
									<li>
										<NavLink
											to="/managed-absences"
											exact
											activeClassName="active"
										>
											<span className="option-text">
												{t("sidebar.managedAbsences")}
											</span>
										</NavLink>
									</li>
								</ul>
							)}
					</li>

					<li>
						<NavLink to="/shift-exchanges" exact activeClassName="active">
							<TipIcon id="tt-shift-exchanges" tip={t("sidebar.shiftExchanges")}>
								<FiRepeat />
							</TipIcon>
							<span className="option-text">{t("sidebar.shiftExchanges")}</span>
						</NavLink>
						{(isAdmin() || isSupervisor()) &&
							(props.context === "shift-exchanges" ||
								props.className === "mobile-sidebar") && (
								<ul className="menu-sub">
									<li>
										<NavLink
											to="/pending-shift-exchanges"
											exact
											activeClassName="active"
										>
											<span className="option-text">
												{t("shiftExchanges.approveRequests")}
											</span>
										</NavLink>
									</li>
									<li>
										<NavLink
											to="/managed-shift-exchanges"
											exact
											activeClassName="active"
										>
											<span className="option-text">
												{t("shiftExchanges.managedRequests")}
											</span>
										</NavLink>
									</li>
								</ul>
							)}
					</li>

					<li>
						{hasMyCalendarSubscription ? (
							<NavLink to="/calendar" exact activeClassName="active">
								<TipIcon id="tt-calendar" tip={t("sidebar.myCalendar")}>
									<FiCalendar />
								</TipIcon>
								<span className="option-text">{t("sidebar.myCalendar")}</span>
							</NavLink>
						) : (
							<a
								href="#"
								onClick={(e) => {
									e.preventDefault();
									setUpgradeFeature(t("sidebar.myCalendar"));
									setShowUpgradeModal(true);
								}}
								className="disabled"
								style={{
									opacity: 0.5,
									cursor: "not-allowed",
								}}
							>
								<FiCalendar />
								<span className="option-text">{t("sidebar.myCalendar")}</span>
							</a>
						)}
					</li>
					<li>
						<NavLink to="/documents" exact activeClassName="active">
							<TipIcon id="tt-documents" tip={t("sidebar.documents")}>
								<FiFolder />
							</TipIcon>
							<span className="option-text">
								{showAdminDocuments
									? t("sidebar.documents")
									: t("sidebar.myDocuments")}
							</span>
						</NavLink>
					</li>

					{!isHaddockUser &&
						(hasExpensesSubscription ? (
							<li>
								<NavLink to="/expenses" exact activeClassName="active">
									<TipIcon id="tt-expenses" tip={t("sidebar.expenses")}>
										<FiCreditCard />
									</TipIcon>
									<span className="option-text">
										{isAdmin() || isSupervisor()
											? t("sidebar.expenses")
											: t("sidebar.myExpenses")}
									</span>
								</NavLink>
							</li>
						) : (
							<li>
								<a
									href="#"
									onClick={(e) => {
										e.preventDefault();
										setUpgradeFeature(t("sidebar.expenses"));
										setShowUpgradeModal(true);
									}}
									className="disabled"
									style={{
										opacity: 0.5,
										cursor: "not-allowed",
									}}
								>
									<FiCreditCard />
									<span className="option-text">
										{isAdmin() || isSupervisor()
											? t("sidebar.expenses")
											: t("sidebar.myExpenses")}
									</span>
								</a>
							</li>
						))}

					{showEmployees && (
						<li>
							<NavLink to="/employees" activeClassName="active">
								<TipIcon id="tt-employees" tip={t("sidebar.employees")}>
									<FiUser />
								</TipIcon>
								<span className="option-text">{t("sidebar.employees")}</span>
							</NavLink>
						</li>
					)}

					{(isAdmin() || isSupervisor()) && (
						<li className="option-settings-mobile-only">
							<NavLink to="/settings" exact activeClassName="active">
								<TipIcon id="tt-config-mobile" tip={t("sidebar.settings")}>
									<FiSettings className="icon-help icon" />
								</TipIcon>

								<span className="option-text">{t("sidebar.settings")}</span>
							</NavLink>
						</li>
					)}

					{canAccessTerminalFichajes && (
						<>
							<hr />
							<li>
								<a
									href="#"
									onClick={(e) => {
										e.preventDefault();
										if (hasClockingTerminalSubscription) {
											handleTerminalAccess();
										} else {
											// Mostrar modal de upgrade si no tiene suscripción adecuada
											setUpgradeFeature(t("sidebar.terminal"));
											setShowUpgradeModal(true);
										}
									}}
									className={!hasClockingTerminalSubscription ? "disabled" : ""}
									style={{
										opacity: !hasClockingTerminalSubscription ? 0.5 : 1,
										cursor: !hasClockingTerminalSubscription
											? "not-allowed"
											: "pointer",
									}}
								>
									<PiTimer />
									<span className="option-text">{t("sidebar.terminal")}</span>
								</a>
							</li>
						</>
					)}

					<li>
						<NavLink to="/notifications" exact activeClassName="active">
							<TipIcon id="tt-notifications" tip={t("sidebar.notifications")}>
								<RxBell />
							</TipIcon>
							<span className="option-text">{t("sidebar.notifications")}</span>
							{unreadNotificationsCount > 0 && (
								<span className="badge badge-unread">
									{unreadNotificationsCount}
								</span>
							)}
						</NavLink>
					</li>
				</ul>
			</div>
			<div className="sidebar-bottom">
				<OnboardingSidebarCard />

				<ul className="menu-core">
					{showConfiguracion && (
						<>
							<hr />
							<li>
								<NavLink to="/settings" exact activeClassName="active">
									<TipIcon id="tt-config" tip={t("sidebar.settings")}>
										<FiSettings className="icon" />
									</TipIcon>

									<span className="option-text">{t("sidebar.settings")}</span>
								</NavLink>
							</li>
						</>
					)}

					{isAdmin() && user?.company?.partner === true && (
						<>
							<li>
								<NavLink to="/login-attempts" exact activeClassName="active">
									<TipIcon
										id="tt-login-attempts"
										tip={t("sidebar.loginAttempts")}
									>
										<FiLock className="icon" />
									</TipIcon>
									<span className="option-text">
										{t("sidebar.loginAttempts")}
									</span>
								</NavLink>
							</li>
							<li>
								<NavLink to="/subscriptions" exact activeClassName="active">
									<TipIcon
										id="tt-subscriptions"
										tip={t("sidebar.subscriptions")}
									>
										<MdOutlineWorkspacePremium className="icon" />
									</TipIcon>

									<span className="option-text">
										{t("sidebar.subscriptions")}
									</span>
								</NavLink>
							</li>
						</>
					)}

					<li>
						<a
							className="link-help"
							title="Ayuda"
							rel="noopener noreferrer"
							target="_blank"
							href={helpUrl}
						>
							<TipIcon id="tt-help" tip={t("sidebar.support")}>
								<FiHelpCircle className="icon" />
							</TipIcon>

							<span className="option-text">{t("sidebar.support")}</span>
						</a>
					</li>
				</ul>

				<div className="divider"></div>

				<OverlayTrigger overlay={popover} trigger="click">
					<div className="sidebar-menu-profile">
						<Avatar
							userId={user.id}
							className="sidebar-avatar avatar-xs"
							alt={user.name}
						/>
						<span className="sidebar-username">
							{user.name} {user.surname}
							<FiChevronRight className="sidebar-menu-arrow" />
						</span>
					</div>
				</OverlayTrigger>
			</div>

			{/* Modal para crear empresa - renderizado con portal fuera del sidebar */}
			{isCreateCompanyModalOpen &&
				createPortal(
					<div
						className="modal fade show create-company-modal"
						style={{ display: "block" }}
					>
						<div className="modal-dialog modal-dialog-centered modal-lg">
							<div className="modal-content">
								<div className="modal-header">
									<h5 className="modal-title">{t("company.create")}</h5>
									<button
										type="button"
										className="close"
										onClick={handleCloseCreateCompanyModal}
										aria-label="Close"
									>
										<span aria-hidden="true">&times;</span>
									</button>
								</div>
								<CreateCompanyModal
									handleClose={handleCloseCreateCompanyModal}
								/>
							</div>
						</div>
						<div className="modal-backdrop fade show"></div>
					</div>,
					document.body
				)}

			{/* Modal de upgrade para funcionalidades bloqueadas */}
			<UpgradeModal
				show={showUpgradeModal}
				onClose={() => setShowUpgradeModal(false)}
				feature={upgradeFeature}
				requiredPlan="Pro"
			/>
		</section>
	);
};

const mapStateToProps = (state) => {
	// console.log(state);
	return {
		user: state.authReducer.user,
	};
};

export default connect(mapStateToProps, null)(withRouter(Sidebar));
